import {css, cx} from "@emotion/css";
import {RefObject, useEffect, useRef, useState} from "react";

import {isNotUndefined, isNotUndefinedOrNull, isUndefinedOrNull, showGoogleAds} from "@/shared/FunctionCore";
import  "@/components/ads/adsStyles.scss";
import React from "react";
import { useGoogleAdAvailability } from "@/components/ads/googleAdsAvailability";


const getWindowGoogletag = () => window["googletag"];

interface AdPropertiesParameter {
    "adDivId": string;
    "adHeight": string;
    "adUnitPath": string;
    "adWidth": string;
}

interface AdPropertiesExtraParameter {
    "lazyLoadAd": boolean;
    "spacingBottom"?: string;
    "spacingTop"?: string;
}

interface AdComponentParameter {
    "adData": AdPropertiesParameter[];
    "properties": AdPropertiesExtraParameter;
}

type AdDataWithAdSizeParsed = AdPropertiesParameter & {
    "height": number;
    "heightWidthClassName": string;
    "lazyLoadAd": boolean;
    "width": number;
};

interface AdProperties {
    "adDataWithAdSizeParsed": AdDataWithAdSizeParsed;
    "windowGoogletag": any;
}

interface GetAdDataWithAdSizeParsed {
    "adDataWithAdSizeParsed": AdDataWithAdSizeParsed | null | undefined;
    "goal": number;
    "windowGoogletag": any;
}

interface AdComponentProperties {
    "adDataWithAdSizeParsed": AdDataWithAdSizeParsed | null | undefined;
    "windowGoogletag": any;
}

const useOnScreen = (reference: RefObject<HTMLDivElement>) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    const observer = new IntersectionObserver((int) => setIsIntersecting(int[0]!.isIntersecting));

    useEffect(() => {
        observer.observe(reference.current!);

        return () => observer.disconnect();
    }, []);

    return isIntersecting;
};

const Ad = (properties: AdProperties) => {
    const [addLoaded, setAddLoaded] = useState(false);

    const referenceObject = useRef<HTMLDivElement>(null);

    const adDataWithAdSizeParsed = properties.adDataWithAdSizeParsed;

    const adDivId = adDataWithAdSizeParsed.adDivId;
    const height = adDataWithAdSizeParsed.height;
    const width = adDataWithAdSizeParsed.width;

    const onScreen = useOnScreen(referenceObject);

    const loadAdd = () => {
        setAddLoaded(true);

        properties.windowGoogletag.cmd.push(() => {
            const newWindowGoogletag = getWindowGoogletag();

            const pubads = newWindowGoogletag.pubads();

            newWindowGoogletag.defineSlot(adDataWithAdSizeParsed.adUnitPath, [width, height], adDivId)?.addService(pubads);

            pubads.enableSingleRequest();

            newWindowGoogletag.enableServices();

            newWindowGoogletag.display(adDivId);
        });
    };

    useEffect(() => {
        if (!adDataWithAdSizeParsed.lazyLoadAd) {
            loadAdd();
        }
    }, []);


    if (onScreen && !addLoaded) {
        loadAdd();
    }

    return <div className={"content"}>
        <div className={`ad ${adDataWithAdSizeParsed.heightWidthClassName}`}
            id={adDivId}
            ref={referenceObject} />
    </div>;
};

const AdComponent = (properties: AdComponentProperties): JSX.Element => {
    const adDataWithAdSizeParsed = properties.adDataWithAdSizeParsed;
    const [advertisingConsentGiven, setAdvertisingConsentGiven] = useState(false);
    const addAvailable = useGoogleAdAvailability();
    const [windowGoogletag, setWindowGoogletag] = useState(false);
    useEffect(() => {
        const displayGoogleAds = () => {
            if (showGoogleAds()) {
                setAdvertisingConsentGiven(true);
                const windowElement = getWindowGoogletag();
                const windowGoogletagRef = isNotUndefined(windowElement) ? windowElement : {"cmd": []};
                setWindowGoogletag(windowGoogletagRef);
            }
        };
        if (addAvailable) {
            displayGoogleAds();
        }
    }, [addAvailable]);


    return (
        (isUndefinedOrNull(adDataWithAdSizeParsed) || !advertisingConsentGiven)
            ? <></>
            :
             <Ad
                adDataWithAdSizeParsed={adDataWithAdSizeParsed}
                windowGoogletag={windowGoogletag} />
    );
};

const getAdDataWithAdSizeParsed = (adComponentParameter: AdComponentParameter): GetAdDataWithAdSizeParsed => {
    const {
        adData,
        properties
    } = adComponentParameter;

    const {
        lazyLoadAd,
        spacingTop
    } = properties;

    const windowElement = getWindowGoogletag();
    const windowGoogletag = isNotUndefined(windowElement) ? windowElement : {"cmd": []};

    const adDataWithAdSizeParsedArray = adData.map((sk) => {
        // TODO
        const height = window.Number(sk.adHeight);
        const width = window.Number(sk.adWidth);
        const marginTop = window.Number(spacingTop);
        const marginBottom = window.Number(spacingTop);
        const AdDataWithAdSizeParsed: AdDataWithAdSizeParsed = {
            ...sk,
            "height": height,
            "heightWidthClassName": css({
                "height": `${height}px`,
                "marginBottom": `${marginBottom}px !important`,
                "marginTop": `${marginTop}px !important`,
                "width": `${width}px`
            }),
            "lazyLoadAd": lazyLoadAd,
            "width": width
        };

        return AdDataWithAdSizeParsed;
    });

    const goal = window.innerWidth;

    let adDataWithAdSizeParsed;

    if (window.Array.isArray(adDataWithAdSizeParsedArray) && adDataWithAdSizeParsedArray.length > 0) {
        adDataWithAdSizeParsed = adDataWithAdSizeParsedArray.reduce((previous, current) => {
            // TODO
            const currentAdWidthNumber = window.Number(current.adWidth);
            const previousAdWidthNumber = window.Number(previous.adWidth);

            const currentAdWidthNumberDifference = window.Math.abs(currentAdWidthNumber - goal);
            const previousAdWidthNumberDifference = window.Math.abs(previousAdWidthNumber - goal);

            if (currentAdWidthNumberDifference === previousAdWidthNumberDifference) {
                return currentAdWidthNumber > previousAdWidthNumber ? current : previous;
            } else {
                return previousAdWidthNumberDifference < currentAdWidthNumberDifference ? previous : current;
            }
        });
    }

    return {"adDataWithAdSizeParsed": adDataWithAdSizeParsed, "goal": goal, "windowGoogletag": windowGoogletag};
};

// const adComponent = (renderableData: RenderableData<AdComponentParameter>): void => {
//     const {
//         adDataWithAdSizeParsed,
//         goal,
//         windowGoogletag
//     } = getAdDataWithAdSizeParsed(renderableData.data);

//     // TODO
//     if (isNotUndefinedOrNull(adDataWithAdSizeParsed) && window.Number(adDataWithAdSizeParsed.adWidth) < goal) {
//         renderIdentifierGeneric(
//             AdComponent,
//             addToRenderableData(
//                 renderableData,
//                 {
//                     "adDataWithAdSizeParsed": adDataWithAdSizeParsed,
//                     "goal": goal,
//                     "windowGoogletag": windowGoogletag
//                 }));
//     }
// };

// export adComponent

const ad = (properties: AdProperties): JSX.Element => {
    const { adComponentParameter } = properties;
  
    if (isNotUndefinedOrNull(adComponentParameter)) {
      const { adDataWithAdSizeParsed, goal, windowGoogletag } =
        getAdDataWithAdSizeParsed(adComponentParameter);
  
      // TODO
      if (
        isNotUndefinedOrNull(adDataWithAdSizeParsed) &&
        window.Number(adDataWithAdSizeParsed.adWidth) < goal
      ) {
        return (
          <AdComponent
            adDataWithAdSizeParsed={adDataWithAdSizeParsed}
            windowGoogletag={windowGoogletag}
          />
        );
      }
    }
    return <></>;
    };

export {AdComponent, AdComponentParameter, AdComponentProperties, getAdDataWithAdSizeParsed, ad};
